import type {
  Concept,
  ConceptId,
  InterviewBlock,
  InterviewConceptSamplingCount,
} from "../types";
import type { SegmentId } from "@/components/responses/segments/types";
import { recordToArray, shuffleArray } from "@/utils/helpers";
import { logger } from "@/utils/logging/isomorphic";

export type LeastFillData = {
  conceptSamplingCount: InterviewConceptSamplingCount;
};

export type BalanceSegmentsData = {
  conceptSamplingCount: InterviewConceptSamplingCount;
  responseSegmentIds: SegmentId[];
  conceptIdToSegmentCounts: Map<ConceptId, Record<SegmentId, number>>;
  segmentCounts: Record<SegmentId, number>;
};

type ConceptSamplingData = LeastFillData | BalanceSegmentsData;

export type AllConceptSamplingData = Partial<
  LeastFillData & BalanceSegmentsData
>;

type ConceptSamplingFunctionParams<TData = ConceptSamplingData> = {
  concepts: Concept[];
  block: InterviewBlock;
  data: TData;
  // seeded rng; when set, sampling is deterministic for a given response + block
  rng?: () => number;
};

type ConceptSamplingFn<TData = ConceptSamplingData> = (
  params: ConceptSamplingFunctionParams<TData>
) => ConceptId[];

export const randomShuffle: ConceptSamplingFn<null> = ({ concepts, rng }) => {
  return shuffleArray(
    concepts.map((c) => c.id),
    rng
  );
};

export const leastFill: ConceptSamplingFn<LeastFillData> = ({
  concepts,
  block,
  data: { conceptSamplingCount },
  rng,
}) => {
  const blockConceptSamplingCount = conceptSamplingCount[block.id] ?? {};

  // Sort concepts by sampling count (lower counts first for more even sampling)
  const grouped = concepts.reduce(
    (acc, concept) => {
      const count = blockConceptSamplingCount[concept.id] || 0;
      acc[count] ??= [];
      acc[count].push(concept);
      return acc;
    },
    {} as Record<number, Concept[]>
  );

  const sortedConcepts = recordToArray(grouped)
    // sort by count ascending
    .sort(([a], [b]) => Number(a) - Number(b))
    // randomize order within each count
    .flatMap(([, ids]) => shuffleArray(ids, rng));

  return sortedConcepts.map((c) => c.id);
};

export const balanceSegments: ConceptSamplingFn<BalanceSegmentsData> = ({
  concepts,
  block,
  data: {
    conceptSamplingCount,
    responseSegmentIds,
    conceptIdToSegmentCounts,
    segmentCounts,
  },
}) => {
  const blockConceptSamplingCount = conceptSamplingCount[block.id] ?? {};

  // For each concept, find the minimum ratio among the participant's segments
  // (i.e., the most underrepresented segment the participant belongs to)
  // Ratio = conceptSegmentCount / totalSegmentCount (proportional representation)
  const getMinSegmentRatio = (conceptId: ConceptId) => {
    const counts = conceptIdToSegmentCounts.get(conceptId);
    if (!counts || responseSegmentIds.length === 0) return 0;
    return Math.min(
      ...responseSegmentIds.map((segmentId) => {
        const conceptSegmentCount = counts[segmentId] ?? 0;
        const totalSegmentCount = segmentCounts[segmentId];
        if (!totalSegmentCount) {
          logger.captureException(
            new Error(
              `Expected segment to have at least 1 response in overall data`
            ),
            { segmentId, conceptId }
          );
          return Infinity;
        }
        return conceptSegmentCount / totalSegmentCount;
      })
    );
  };

  // Get overall sampling count for tie-breaking
  const getSamplingCount = (conceptId: ConceptId) =>
    blockConceptSamplingCount[conceptId] ?? 0;

  return [...concepts]
    .sort((a, b) => {
      const ratioDiff = getMinSegmentRatio(a.id) - getMinSegmentRatio(b.id);
      if (ratioDiff !== 0) return ratioDiff;
      // Tie-break using least fill (prefer concepts with fewer overall responses)
      return getSamplingCount(a.id) - getSamplingCount(b.id);
    })
    .map((c) => c.id);
};
